import { useState } from "react";
import { Tag } from "antd";
import { useTranslation } from "react-i18next";
import { useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { FIELD_REGISTRY } from "utils/api/graphql/queries/screens";

const TYPE_COLORS = {
  text: "blue",
  input: "orange",
  action: "purple",
  image: "cyan",
  list: "green",
};

const FILTER_KEYS = ["all", "input", "action", "text", "image", "list"];

const SlotList = ({ slots, selectedSlot, onSelect, processId }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.SCREEN_STUDIO);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const SLOT_LIST = DETAIL.SLOT_LIST || {};
  const TYPE_LABELS = SLOT_LIST.TYPES || {};
  const FILTER_LABELS = SLOT_LIST.FILTERS || {};
  const [filter, setFilter] = useState("all");
  const { data: registryData } = useQuery(FIELD_REGISTRY, {
    variables: processId ? { process: { id: processId } } : {},
  });
  const registry = registryData?.fieldRegistry || [];

  if (!slots || slots.length === 0) {
    return (
      <div className="screen-studio__versions">
        <h4>{SLOT_LIST.TITLE || "Slots"}</h4>
        <p className="screen-studio__slot-list-empty">{SLOT_LIST.EMPTY}</p>
      </div>
    );
  }

  const filtered =
    filter === "all" ? slots : slots.filter((s) => s.type === filter);
  const bound = filtered.filter(
    (s) =>
      s.binding &&
      s.binding.kind !== "unbound" &&
      s.binding.kind !== "static_i18n",
  );
  const unbound = filtered.filter(
    (s) =>
      !s.binding ||
      s.binding.kind === "unbound" ||
      s.binding.kind === "static_i18n",
  );

  return (
    <div className="screen-studio__versions screen-studio__slot-list">
      <h4>
        {SLOT_LIST.TITLE_COUNT
          ? SLOT_LIST.TITLE_COUNT.replace("{{count}}", String(slots.length))
          : `Slots (${slots.length})`}
      </h4>
      <div className="screen-studio__slot-filters d-flex flex--wrap gap-4 mb-8">
        {FILTER_KEYS.map((key) => {
          const count =
            key === "all"
              ? slots.length
              : slots.filter((s) => s.type === key).length;
          if (key !== "all" && count === 0) return null;
          return (
            <Tag
              key={key}
              color={filter === key ? TYPE_COLORS[key] || "blue" : "default"}
              className="screen-studio__slot-filter-tag"
              onClick={() => setFilter(key)}
            >
              {FILTER_LABELS[key] || key} ({count})
            </Tag>
          );
        })}
      </div>

      {unbound.length > 0 && (
        <div className="mb-8">
          <small className="screen-studio__slot-section-label">
            {SLOT_LIST.TO_CONFIGURE
              ? SLOT_LIST.TO_CONFIGURE.replace(
                  "{{count}}",
                  String(unbound.length),
                )
              : unbound.length}
          </small>
        </div>
      )}

      {unbound.map((slot) => (
        <SlotRow
          key={slot.id}
          slot={slot}
          isSelected={slot.id === selectedSlot}
          onSelect={onSelect}
          registry={registry}
          typeLabels={TYPE_LABELS}
        />
      ))}

      {bound.length > 0 && (
        <div className="mt-12 mb-8">
          <small className="screen-studio__slot-section-label screen-studio__slot-section-label--configured">
            {SLOT_LIST.CONFIGURED
              ? SLOT_LIST.CONFIGURED.replace("{{count}}", String(bound.length))
              : bound.length}
          </small>
        </div>
      )}

      {bound.map((slot) => (
        <SlotRow
          key={slot.id}
          slot={slot}
          isSelected={slot.id === selectedSlot}
          onSelect={onSelect}
          registry={registry}
          typeLabels={TYPE_LABELS}
        />
      ))}
    </div>
  );
};

const lookupFieldLabel = (registry, entityKey, fieldKey) => {
  const group = registry.find((g) => g.key === entityKey);
  if (!group) return null;
  const field = group.fields.find((f) => f.key === fieldKey);
  return field ? `${entityKey}.${fieldKey}` : null;
};

const SlotRow = ({ slot, isSelected, onSelect, registry, typeLabels }) => {
  const registryLabel =
    slot.binding?.kind === "entity_field" && registry
      ? lookupFieldLabel(registry, slot.binding.entity, slot.binding.field)
      : null;

  const fieldLabel =
    registryLabel ||
    (slot.binding?.kind === "entity_field" && slot.binding.field
      ? slot.binding.field
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, function (c) {
            return c.toUpperCase();
          })
          .trim()
      : null);

  const preview =
    fieldLabel ||
    slot.label ||
    slot.binding?.sourceValue ||
    slot.inputKind ||
    slot.type;

  const bindingInfo =
    slot.binding?.kind === "entity_field"
      ? slot.binding.entity + "." + slot.binding.field
      : slot.binding?.kind === "process_trigger"
        ? "trigger: " + slot.binding.name
        : "";

  const tooltip = [
    fieldLabel ? fieldLabel + " (" + bindingInfo + ")" : bindingInfo,
    slot.binding?.sourceValue,
    slot.inputKind ? "type: " + slot.inputKind : "",
  ]
    .filter(Boolean)
    .join(" | ");

  return (
    <div
      className={
        "screen-studio__version-item screen-studio__slot-row" +
        (isSelected ? " screen-studio__version-item--active" : "")
      }
      onClick={() => onSelect(slot.id)}
      title={tooltip}
    >
      <div className="d-flex align--center gap-4">
        <Tag
          color={TYPE_COLORS[slot.type] || "default"}
          className="screen-studio__slot-type-tag"
        >
          {typeLabels[slot.type] || slot.type}
        </Tag>
        <span className="screen-studio__slot-preview">
          {preview.length > 30 ? preview.slice(0, 30) + "…" : preview}
        </span>
      </div>
      <div className="screen-studio__slot-id">
        {slot.id}
        {bindingInfo ? " → " + bindingInfo : ""}
      </div>
    </div>
  );
};

export default SlotList;
